import { Injectable, inject } from '@angular/core';

import { ApiService } from '../../api/api.service';
import { MembershipService } from '../membership.service';

/**
 * MembershipOfferingService (Angular 18)
 *
 * Ported 1:1 from apps/b2b-ui/common/services/membership/offering/offering.service.js
 * (legacy AngularJS name 'MembershipOfferingService'). Drives the multi-step
 * offering create/edit flow: holds details/policies/lifecycle staging state,
 * merges them into a single model, and proxies rule-engine CRUD calls.
 *
 * Dependency mapping:
 *  - ApiService → Angular 18 (injected directly; rulesEngine.rule.* calls).
 *  - MembershipService → now Angular 18 (injected directly for findSalesChannel).
 *  - `$q.allSettled(...)` → native `Promise.allSettled(...)`: the ApiService
 *    facade returns native promises and the settled results are discarded, so
 *    the behaviour (clear deletedRules once every removal settles) is identical.
 *
 * Registered back into AngularJS under 'MembershipOfferingService' via
 * downgradeInjectable in app/upgrade/ng-downgraded-components.ts. Wave 3 #28.
 */
@Injectable({ providedIn: 'root' })
export class MembershipOfferingService {
  private apiService = inject(ApiService);
  private membershipService = inject(MembershipService);

  model: any = {};
  details: any = {};
  policies: any = {};
  lifecycle: any = {};
  deletedRules: any[] = [];

  ready = {
    details: false,
    policies: false,
    lifecycle: false,
  };

  loaded = {
    details: false,
    policies: false,
    lifecycle: false,
  };

  init(): boolean {
    return true;
  }

  unReady(): void {
    this.ready.details = false;
    this.ready.policies = false;
    this.ready.lifecycle = false;
  }

  unLoaded(): void {
    this.loaded.details = false;
    this.loaded.policies = false;
    this.loaded.lifecycle = false;
  }

  createRule(rule: any): Promise<any> {
    return this.apiService.rulesEngine.rule.create({
      templateId: rule.id,
    });
  }

  updateRule(rule: any): Promise<any> {
    return this.apiService.rulesEngine.rule.updateTrigger({
      id: rule.id,
      startMonth: rule.start,
      endMonth: rule.end,
      monthsBeforeEnd: undefined,
      expirationBased: undefined,
    });
  }

  removeRule(rule: any): Promise<any> {
    return this.apiService.rulesEngine.rule.remove({ id: rule.id });
  }

  purgeRules(): void {
    let i, ii;
    const promises: Promise<any>[] = [];

    for (i = 0, ii = this.deletedRules.length; i < ii; i++) {
      promises.push(this.removeRule(this.deletedRules[i]));
    }

    Promise.allSettled(promises).then((response) => {
      let _response;

      while ((_response = response.pop())) {
        void _response;
      }

      this.deletedRules = [];
    });
  }

  durationToMonths(duration?: any): number {
    let r = 0;
    let years = 0;
    let months = 0;

    if (duration) {
      if (duration.years || duration.years > 0) {
        years = parseInt(duration.years, 10);
      }
      if (duration.months || duration.years > 0) {
        months = parseInt(duration.months, 10);
      }

      if (!isNaN(years) && !isNaN(months)) {
        r = years * 12 + months;
      }
    }

    return r;
  }

  monthsToDuration(months?: any): { years: number; months: number } {
    const r = {
      years: 0,
      months: 0,
    };
    const _years = parseInt(String(months / 12), 10);
    const _months = parseInt(String(months % 12), 10);

    if (!isNaN(_months) && !isNaN(_years)) {
      r.years = _years;
      r.months = _months;
    }

    return r;
  }

  merge(): void {
    let i, ii;
    let j, jj;
    let action;
    const actions: any[] = [];

    for (i = 0, ii = this.policies.length; i < ii; i++) {
      action = this.migrateSalesChannelOptions(this.policies[i]);
      action.ruleIds = [];

      for (j = 0, jj = this.lifecycle.length; j < jj; j++) {
        if (this.policies[i].action === this.lifecycle[j].action) {
          action.ruleIds = this.lifecycle[j].ruleIds;
          break;
        }
      }

      actions.push(action);
    }

    this.model = this.details;
    this.model.actions = actions;
  }

  findConfirmationEmailRestriction(confirmationEmailRestrictions: any[], saleChannel: any): any {
    let i, ii;
    let r: any = false;

    for (i = 0, ii = confirmationEmailRestrictions.length; i < ii; i++) {
      if (confirmationEmailRestrictions[i].saleChannel === saleChannel) {
        r = {
          restriction: confirmationEmailRestrictions[i],
          index: i,
        };
        break;
      }
    }

    return r;
  }

  migrateSalesChannelOptions(policy: any): any {
    let i, ii;
    let foundSaleChannel;
    let foundRestriction;

    for (i = 0, ii = policy.salesChannelOptions.length; i < ii; i++) {
      if (policy.salesChannelOptions[i].saleChannelEntity) {
        foundSaleChannel = this.membershipService.findSalesChannel(
          policy.salesChannels,
          policy.salesChannelOptions[i].saleChannelEntity.channel,
        );
        if (foundSaleChannel) {
          policy.salesChannels[foundSaleChannel.index].enabled =
            policy.salesChannelOptions[i].saleChannelEntity.enabled;
        }
      }

      foundRestriction = this.findConfirmationEmailRestriction(
        policy.confirmationEmailRestrictions,
        policy.salesChannelOptions[i].saleChannel,
      );
      if (foundRestriction) {
        policy.confirmationEmailRestrictions[foundRestriction.index].canSendConfirmationEmail =
          policy.salesChannelOptions[i].canSendConfirmationEmail;
      }
    }

    return policy;
  }

  getModel(): any {
    this.merge();

    /**
     * Makes sure there is only ever 1 price point per offering
     * @link https://acmeticketing.atlassian.net/browse/ENG-13785
     */
    this.model.priceLists.forEach((priceList: any) => {
      priceList.prices = priceList.prices.slice(0, 1);
    });

    return this.model;
  }
}
